<template>
  <div>
    <navbar :links="['dashboard', $route.name]" />
    <!-- Header Logo and Buttons -->
    <v-layout
      row
      justify-center
    >
      <v-layout
        column
        align-center
        class="logo"
      >
        <div class="mt-4 mb-2">
          <div
            class="logo clickable"
            @click="goToWebsite()"
          >
            <img src="/static/logo_myshopradio_tv_black.svg">
          </div>
        </div>
        <v-layout
          align-center
          wrap
        >
          <v-btn
            round
            flat
            dark
            class="msr--text my-1"
            @click="goToWebsite()"
          >
            {{ $t('website') }}
          </v-btn>
          <div style="width: 1em" />
          <v-btn
            round
            flat
            dark
            class="msr--text my-1"
            @click="goToDataPrivacyPolicy()"
          >
            {{ $t('privacyPolicy') }}
          </v-btn>
          <v-btn
            round
            flat
            dark
            class="msr--text my-1"
            @click="goToImprintPage()"
          >
            {{ $t('imprint') }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-layout>
    <!-- Support -->
    <v-subheader>Support</v-subheader>
    <v-divider />
    <v-layout
      align-center
      wrap
    >
      <!-- Hotline + Email -->
      <div>
        <v-text-field
          :value="hotline"
          prepend-icon="phone"
          disabled
          class="v-text-custom-disabled v-text-anchor"
          @click.native="tel(hotline)"
        />
        <v-text-field
          :value="email"
          prepend-icon="mail"
          disabled
          class="mt-0 v-text-custom-disabled v-text-anchor"
          @click.native="mailTo(email)"
        />
      </div>
      <!-- Whatsapp -->
      <v-layout
        class="ma-2"
        align-center
        justify-center
        wrap
      >
        <a
          :href="personalizedWhatsappLink"
          :title="$t('whatsappOpenTooltip')"
          target="_blank"
          class="whatsapp-link scale-on-hover"
        >
          <v-layout align-center>
            <img
              src="/static/whatsapp_logo.png"
              :height="$vuetify.breakpoint.mdAndUp ? 132 : 66"
              class="mr-2"
            >
            <canvas
              v-if="$vuetify.breakpoint.mdAndUp"
              ref="qrcode"
              style="
                max-width: 150px;
                max-height: 150px;
                min-width: 150px;
                min-height: 150px;
              "
            />
          </v-layout>
          <small class="grey--text">{{ $t('whatsappOpenHint') }}</small>
        </a>
        <v-layout
          column
          justify-center
          align-center
          class="grey--text text--darken-3"
          style="max-width: 382px; line-height: 1.25"
          :style="{transform: $vuetify.breakpoint.mdAndUp ? '' : 'scale(0.75)'}"
        >
          <span
            class="nowrap"
            style="font-size: 1.6em"
          >{{
            $t('whatsappServiceSupport')
          }}</span>
          <span
            class="font-weight-bold nowrap"
            style="font-size: 3.65em"
          >{{
            whatsappPhoneNumberGerman
          }}</span>
          <span
            class="nowrap"
            style="font-size: 1.25em"
          >{{ $t('whatsappInternationalNumberPrefix') }}:
            {{ whatsappPhoneNumberInternational }}</span>
        </v-layout>
      </v-layout>
    </v-layout>
    <!-- About -->
    <v-subheader>{{ $t('about') }}</v-subheader>
    <v-divider class="mb-2" />
    <v-layout row>
      <v-text-field
        :label="$t('appRevision')"
        :value="revision"
        prepend-icon="build"
        disabled
        class="v-text-custom-disabled mr-1"
      />
    </v-layout>
    <v-text-field
      :label="$t('detectedEnvironment')"
      :value="runtimesString"
      prepend-icon="perm_device_information"
      disabled
      class="v-text-custom-disabled"
    />
    <v-layout wrap>
      <div class="grow-1 nowrap mb-3">
        <v-layout align-center>
          <v-icon
            medium
            class="mr-3"
          >
            share
          </v-icon>
          <v-layout column>
            <span class="link-label">{{ $t('usesIconsFrom') }}</span>
            <a
              href="https://icons8.com/"
              class="black--text"
            > Icons8 </a>
          </v-layout>
        </v-layout>
      </div>
      <div class="grow-1 nowrap mb-3">
        <v-layout align-center>
          <v-icon
            medium
            class="mr-3"
          >
            share
          </v-icon>
          <v-layout column>
            <span class="link-label">{{ $t('usesIconsFrom') }}</span>
            <a
              href="https://www.freepik.com/"
              class="black--text"
            > Freepik </a>
          </v-layout>
        </v-layout>
      </div>
      <div class="grow-1 nowrap mb-3">
        <v-layout align-center>
          <v-icon
            medium
            class="mr-3"
          >
            share
          </v-icon>
          <v-layout column>
            <span class="link-label">{{ $t('usesAnimationsFrom') }}</span>
            <a
              href="https://lottiefiles.com/"
              class="black--text"
            >
              LottieFiles
            </a>
          </v-layout>
        </v-layout>
      </div>
    </v-layout>
    <!-- Empty placeholder to allow a bit more scrolling -->
    <div style="height: 50px" />
  </div>
</template>

<script>
import QRCode from 'qrcode';

import {SUPPORT_HOTLINE, SUPPORT_EMAIL} from '@/store';

import Navbar from '@/components/Navbar';

// Our main local phone no. is now connected to WhatsApp via https://www.hellomateo.de
const WHATSAPP_NUMBER_INTERNATIONAL_LABEL = '0049 521 1639270';
const WHATSAPP_NUMBER_GERMAN_LABEL = '0521-1639270';
const WHATSAPP_LINK = 'https://wa.me/495211639270';

export default {
  components: {
    Navbar,
  },
  data() {
    return {
      hotline: SUPPORT_HOTLINE,
      email: SUPPORT_EMAIL,
      whatsappPhoneNumberInternational: WHATSAPP_NUMBER_INTERNATIONAL_LABEL,
      whatsappPhoneNumberGerman: WHATSAPP_NUMBER_GERMAN_LABEL,
    };
  },
  computed: {
    revision() {
      return this.$store.state.common.revision;
    },
    runtimesString() {
      let names = [];
      const runtime = this.$store.state.common.runtimeEnv;

      for (let name in runtime) {
        if (runtime.hasOwnProperty(name)) {
          const isThatRuntime = runtime[name] === true;
          if (isThatRuntime) {
            if (name !== 'iOS') {
              // Capitalize the browser name for display.
              name = name.charAt(0).toUpperCase() + name.slice(1);
            }
            names.push(name);
          }
        }
      }
      return names.join(', ');
    },
    personalizedWhatsappLink() {
      if (!this.$user) {
        return WHATSAPP_LINK;
      }

      // User first/lastname or email.
      let name =
        (this.$user.first_name || '').trim() +
        ' ' +
        (this.$user.last_name || '').trim();
      if (name.trim() == '') {
        name = this.$user.email;
      }

      // Also add company/workspace name.
      if (this.$userprofile.workspace) {
        name += ' (' + this.$userprofile.workspace.name + ')';
      }

      const prefix =
        this.$t('whatsappMessagePrefixTemplate', {name: name}) + ': ';
      return WHATSAPP_LINK + '?text=' + encodeURIComponent(prefix);
    },
  },
  watch: {
    personalizedWhatsappLink: function () {
      this.initializeWhatsappQrCode();
    },
  },
  mounted() {
    this.$store.dispatch('RETRIEVE_REVISION');
    this.initializeWhatsappQrCode();
  },
  methods: {
    initializeWhatsappQrCode() {
      if (!this.$refs.qrcode) {
        return;
      }
      QRCode.toCanvas(this.$refs.qrcode, this.personalizedWhatsappLink);
    },
    goToWebsite() {
      this.$bus.$emit('alert-info', {message: this.$t('redirect')});
      setTimeout(() => {
        window.location.href = this.$store.state.common.websiteURL;
      }, 750);
    },
    goToDataPrivacyPolicy() {
      this.$bus.$emit('alert-info', {message: this.$t('redirect')});
      setTimeout(() => {
        window.location.href = this.$store.state.common.dataPrivacyURL;
      }, 750);
    },
    goToImprintPage() {
      this.$bus.$emit('alert-info', {message: this.$t('redirect')});
      setTimeout(() => {
        window.location.href = this.$store.state.common.imprintURL;
      }, 750);
    },
    tel(phoneNumber) {
      location.href = `tel:${phoneNumber}`;
    },
    mailTo(email) {
      location.href = `mailto:${email}`;
    },
  },
};
</script>

<style scoped>
.logo {
  width: calc(320px - 48px);
  height: auto;
}

.link-label {
  font-size: 12px;
  margin-bottom: 4px;
  color: rgba(0, 0, 0, 0.75);
}

.scale-on-hover:hover {
  transform: scale(1.03);
  transition: all 150ms ease;
}

a.whatsapp-link:link {
  text-decoration: none;
}
a.whatsapp-link:visited {
  text-decoration: none;
}
a.whatsapp-link:hover {
  text-decoration: none;
}
a.whatsapp-link:active {
  text-decoration: none;
}
</style>



// WEBPACK FOOTER //
// src/pages/About.vue